import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mountain, Flower2, LayoutGrid } from 'lucide-react';
import { projects } from '../mockData';

const ProjectsPage = () => {
  // Default to 'All' or one of your new strategic categories
  const [activeTab, setActiveTab] = useState('All');

  // Helper to map old categories to new strategic buckets if needed
  // This ensures old data still shows up even before you fully re-tag everything
  const getStrategicCategory = (project) => {
    if (project.category === 'Ecological Restoration' || project.category === 'Landscape Design') {
      return project.category;
    }
    // Fallback logic for older data
    if (['Maintenance', 'Sustainable', 'Commercial'].includes(project.category)) return 'Ecological Restoration';
    return 'Landscape Design';
  };

  const filteredProjects = activeTab === 'All'
    ? projects
    : projects.filter(project => project.category === activeTab);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1E5631] to-[#2d7a47] py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1595387426256-cc153122a6f1"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl text-white">
            <span className="inline-block bg-[#4CAF50] px-4 py-2 rounded-full text-sm font-semibold mb-4">
              Our Portfolio
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-bold mb-6">
              Transforming Landscapes
            </h1>
            <p className="text-xl text-green-100 leading-relaxed">
              From barren mining lands to thriving urban sanctuaries.
            </p>
          </div>
        </div>
      </section>

      {/* Strategic Tabs */}
      <section className="py-8 bg-white border-b border-gray-100 sticky top-20 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap justify-center gap-4">
          
          <button
            onClick={() => setActiveTab('All')}
            className={`flex items-center px-6 py-3 rounded-full font-bold transition-all duration-300 ${
              activeTab === 'All'
                ? 'bg-[#1E5631] text-white shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <LayoutGrid className="w-4 h-4 mr-2" />
            All Projects
          </button>

          <button
            onClick={() => setActiveTab('Ecological Restoration')}
            className={`flex items-center px-6 py-3 rounded-full font-bold transition-all duration-300 ${
              activeTab === 'Ecological Restoration'
                ? 'bg-[#1E5631] text-white shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Mountain className="w-4 h-4 mr-2" />
            Ecological Restoration (Mining/CSR)
          </button>

          <button
            onClick={() => setActiveTab('Landscape Design')}
            className={`flex items-center px-6 py-3 rounded-full font-bold transition-all duration-300 ${
              activeTab === 'Landscape Design'
                ? 'bg-[#1E5631] text-white shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Flower2 className="w-4 h-4 mr-2" />
            Luxury Landscape Design
          </button>

        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.length > 0 ? (
              filteredProjects.map((project) => (
                <div
                  key={project.id}
                  className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 bg-white transform hover:-translate-y-2"
                >
                  <div className="relative h-80 overflow-hidden">
                    <img
                      src={`${project.image}?auto=format&fit=crop&w=600&q=70`}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end opacity-90 transition-opacity duration-300">
                      <div className="p-6 text-white w-full">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-3 ${
                          project.category === 'Ecological Restoration' ? 'bg-blue-600' : 'bg-[#4CAF50]'
                        }`}>
                          {project.category}
                        </span>
                        <h3 className="font-heading text-2xl font-bold mb-2 leading-tight">{project.title}</h3>
                        <p className="text-gray-200 text-sm line-clamp-2">{project.description}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full text-center py-20">
                <p className="text-xl text-gray-500">No projects found in this category yet.</p>
                <button 
                  onClick={() => setActiveTab('All')} 
                  className="mt-4 text-[#1E5631] font-bold hover:underline"
                >
                  View All Projects
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Before/After Showcase */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#4CAF50] font-semibold text-sm uppercase tracking-wide mb-4">
              Real Results
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Before & After
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Visual proof of our engineering and design capabilities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {projects.slice(0, 2).map((project) => (
              <div key={project.id} className="bg-gray-50 rounded-2xl overflow-hidden shadow-lg border border-gray-100">
                <div className="grid grid-cols-2 relative">
                  {/* Before Image */}
                  <div className="relative group">
                    <img
                      src={`${project.beforeImage}?auto=format&fit=crop&w=600&q=70`}
                      alt="Before"
                      className="w-full h-64 object-cover"
                    />
                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-md text-xs font-bold text-gray-900 shadow-sm">
                      BEFORE
                    </div>
                  </div>
                  
                  {/* After Image */}
                  <div className="relative group">
                    <img
                      src={`${project.image}?auto=format&fit=crop&w=600&q=70`}
                      alt="After"
                      className="w-full h-64 object-cover"
                    />
                    <div className="absolute top-4 right-4 bg-[#4CAF50] px-3 py-1 rounded-md text-xs font-bold text-white shadow-sm">
                      AFTER
                    </div>
                  </div>
                  
                  {/* Divider Line */}
                  <div className="absolute inset-y-0 left-1/2 w-1 bg-white z-10"></div>
                </div>
                
                <div className="p-8">
                  <h3 className="font-heading text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-[#1E5631] to-[#2d7a47] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1562886695-0f3c22a48bbb"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Space?
          </h2>
          <p className="text-xl text-green-100 mb-8">
            Whether it's a mine site or a home garden, we have the expertise.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center bg-[#4CAF50] text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#45a049] transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
          >
            Start Your Project
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProjectsPage;